/**
 * Image-edit pipeline for staging.
 *
 * Default production backend: OpenAI GPT Image 2.5 Sunburst via the
 * shared `openai-image` Supabase Edge Function (`OPENAI_API_KEY`).
 *
 * Gemini Pro / Flash remain available as admin/dev overrides through the
 * model picker (routed via `gemini-image`).
 *
 * Same-room multi-angle also uses text modes on the Gemini edge function:
 * `extract-inventory` and `verify-furniture`.
 */

import type { FurnitureInventory } from '../types';
import { formatFurnitureInventoryChecklist, formatPlacementPlan, isFullRenovationPrompt } from '../constants';

const SUPABASE_URL = process.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.VITE_SUPABASE_ANON_KEY || '';
const FN_URL = `${SUPABASE_URL}/functions/v1/gemini-image`;
const OPENAI_FN_URL = `${SUPABASE_URL}/functions/v1/openai-image`;

/** Image backends. Sunburst is production; Gemini options are admin overrides. */
export type StagingImageModelId = 'gpt-sunburst' | 'gemini-pro' | 'gemini-flash';

export const DEFAULT_STAGING_IMAGE_MODEL: StagingImageModelId = 'gpt-sunburst';

export const STAGING_IMAGE_MODELS: Array<{
  id: StagingImageModelId;
  label: string;
  shortLabel: string;
  description: string;
}> = [
  {
    id: 'gpt-sunburst',
    label: 'GPT Image 2.5 Sunburst',
    shortLabel: 'Sunburst',
    description: 'Production default — consistent renovations and openings.',
  },
  {
    id: 'gemini-pro',
    label: 'Gemini 3 Pro Image',
    shortLabel: 'Pro',
    description: 'Admin override — Gemini Pro (variable results).',
  },
  {
    id: 'gemini-flash',
    label: 'Gemini 3.1 Flash Image',
    shortLabel: 'Flash',
    description: 'Admin override — faster Gemini Flash.',
  },
];

const STORAGE_KEY = 'staging.imageModel.v2';

let activeImageModel: StagingImageModelId = DEFAULT_STAGING_IMAGE_MODEL;

try {
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem(STORAGE_KEY) as StagingImageModelId | null;
    if (saved && STAGING_IMAGE_MODELS.some((m) => m.id === saved)) {
      activeImageModel = saved;
    }
  }
} catch {
  // ignore
}

export const getStagingImageModel = (): StagingImageModelId => activeImageModel;

export const setStagingImageModel = (id: StagingImageModelId): void => {
  if (!STAGING_IMAGE_MODELS.some((m) => m.id === id)) return;
  activeImageModel = id;
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // ignore
  }
};

const geminiModelForTrial = (id: StagingImageModelId): string | undefined => {
  if (id === 'gemini-flash') return 'gemini-3.1-flash-image';
  // Pro uses the edge function default (gemini-3-pro-image-preview).
  return undefined;
};

const MAX_RETRIES = 3;
const INITIAL_DELAY_MS = 2000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const isRetryableError = (msg: string): boolean =>
  /429|rate.?limit|quota|RESOURCE_EXHAUSTED|temporarily unavailable|capacity|timed?[- ]out|interrupted|\(code:\s*PA\)|502|503|504/i.test(
    msg,
  );

/** True if this is a virtual staging prompt (room must be preserved, only add furniture).
 *  Matches the 360 lead ("Virtually stage this room") and the flat lead
 *  ("...add <style> furniture and movable decor into its empty floor space..."). */
const isStagingPrompt = (text: string): boolean =>
  /Virtually stage|stage this room|stage the room|furniture and movable decor into its empty floor space/i.test(
    text,
  );

/**
 * True for any edit where the fixed architecture (doors, doorways,
 * windows, walls, built-ins) must survive untouched: staging, clear
 * furniture, outdoor staging, match-style, and refine passes. These are
 * the edits that go through the post-generation structural inspection.
 */
const isStructurePreservingEdit = (text: string): boolean =>
  !isFullRenovationPrompt(text) &&
  /Virtually stage|stage this room|stage the room|furniture and movable decor into its empty floor space|Remove ONLY movable furniture|already been virtually staged/i.test(
    text,
  );

/**
 * Shared color guardrails to prevent iterative passes from drifting
 * toward any global colour cast. Gemini has a tendency to push warm /
 * magenta on each pass; these guardrails keep neutrals neutral.
 */
const COLOR_FIDELITY_REQUIREMENTS =
  ' CRITICAL COLOR FIDELITY:' +
  ' (1) Preserve the EXACT white balance and colour temperature of the source photo — do not warm it up, cool it down, or shift its tint in any way.' +
  ' (2) Keep all near-neutral surfaces (white/cream walls, ceilings, grey floors, white trim, white worktops) at the SAME chromaticity as in the source. A cream wall must stay the same cream. A grey floor must stay the same grey. Off-white must NOT become pink, peach, lavender, or yellow.' +
  ' (3) Wood floors and wood furniture in the SOURCE must keep the exact same wood tone and hue — do not push them redder, pinker, more orange, or more magenta.' +
  ' (4) Do NOT introduce ANY global colour cast — no warm/red/orange/yellow shift, no magenta/purple/pink shift, no cool/blue/green shift. The output must be visually colour-matched to the source as if shot on the same camera under the same lighting.' +
  ' (5) Treat colour fidelity as more important than aesthetic enhancement: do NOT "improve" the colours, do NOT make the room feel warmer or cosier, do NOT add a "real-estate marketing" glow. Same colours in, same colours out.';

/**
 * Fetch a hosted image URL and convert it to a base64 data URL so the
 * caller can keep treating outputs as inline data, matching the
 * previous Gemini contract (used by App.tsx, Editor.tsx, etc.).
 */
async function urlToDataUrl(url: string): Promise<string> {
  if (url.startsWith('data:')) return url;
  const r = await fetch(url);
  if (!r.ok) throw new Error(`Failed to fetch generated image: ${r.status}`);
  const blob = await r.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read generated image as data URL'));
    reader.readAsDataURL(blob);
  });
}

// Gemini accepts: 1:1, 3:4, 4:3, 9:16, 16:9. Anything else (e.g. 21:9
// used by padded equirectangular 360 panoramas, or "match_input_image")
// is passed through as `match_input_image`; the edge function drops it
// from the imageConfig so Gemini infers the ratio from the input — our
// cropResultToOriginal step then restores exact original dimensions.
function normaliseAspect(a?: string): string {
  if (!a) return 'match_input_image';
  const allowed = new Set(['1:1', '3:4', '4:3', '9:16', '16:9']);
  return allowed.has(a) ? a : 'match_input_image';
}

interface RunOpts {
  prompt: string;
  images: string[]; // data: URLs or https URLs, up to 14
  aspectRatio?: string;
  /** Output resolution bucket ('1K' | '2K' | '4K'). Defaults to '4K'. */
  resolution?: string;
}

/**
 * Pick the Gemini output resolution bucket that best matches the input's
 * native size WITHOUT forcing an upscale.
 *
 * Why this matters for wall/perspective fidelity: `gemini-3-pro-image-preview`
 * is a full re-render model, and asking it for a 4K output from a smaller
 * input forces it to SYNTHESISE detail — effectively re-drawing the whole
 * room, which is a common source of walls shifting and the camera/perspective
 * drifting. Matching the output resolution to the input keeps the generation
 * as close to a true in-place edit as the model allows. Genuinely large
 * uploads still get 4K.
 *
 * Falls back to '4K' if the image can't be measured.
 */
async function resolutionForImage(dataUrl: string): Promise<'1K' | '2K' | '4K'> {
  try {
    const img = await loadImage(dataUrl);
    const longEdge = Math.max(img.width, img.height);
    if (longEdge <= 1024) return '1K';
    if (longEdge <= 2048) return '2K';
    return '4K';
  } catch {
    return '4K';
  }
}

// === POST-GENERATION STRUCTURAL VERIFICATION ===
//
// Prompt instructions alone do not reliably stop Gemini from deleting
// doors / doorways / windows while staging (observed failure: an open
// door into a hallway replaced by a wall + mirror). So after every
// structure-preserving generation we run a cheap Gemini Flash
// "inspector" pass (mode: 'verify-structure' on the same edge function)
// that compares the original and the result, looking ONLY at fixed
// architecture. If it finds violations, we regenerate with the specific
// violations fed back into the prompt, up to MAX_STRUCTURE_RETRIES
// extra attempts. The check FAILS OPEN: if the inspector itself errors,
// the generation is returned as-is rather than blocking the user.

/**
 * Extra generation attempts after a failed structural inspection.
 *
 * Raised from 2 → 4: perspective/wall drift is the most-reported failure and
 * a single regeneration often still drifts. Each retry costs one more image
 * generation plus one `gemini-2.5-pro` inspection, but that is an acceptable
 * trade for materially higher structural fidelity. The check still FAILS OPEN
 * — the last attempt is returned even if imperfect, so a user is never blocked.
 */
const MAX_STRUCTURE_RETRIES = 4;

/** Extra attempts after a failed furniture-inventory checklist inspection. */
const MAX_FURNITURE_RETRIES = 2;

/** Long edge used for the inspection images — structure is perfectly
 * visible at 1024px and it keeps the verify payload/latency small. */
const VERIFY_MAX_DIM = 1024;

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image for verification'));
    img.src = src;
  });

async function downscaleForVerify(dataUrl: string): Promise<string> {
  const img = await loadImage(dataUrl);
  const scale = Math.min(1, VERIFY_MAX_DIM / Math.max(img.width, img.height));
  if (scale === 1 && dataUrl.startsWith('data:image/jpeg')) return dataUrl;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.width * scale));
  canvas.height = Math.max(1, Math.round(img.height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.85);
}

interface StructureVerdict {
  ok: boolean;
  violations: string[];
}

/**
 * Verdict of the most recent structure-checked generation. The app is
 * strictly one-generation-at-a-time, so a module-level slot is safe. Used
 * by App.tsx to refuse a structurally-broken hero as the same-room batch
 * anchor (a re-rendered hero poisons every follow-up angle).
 */
let lastStructureVerdict: StructureVerdict = { ok: true, violations: [] };
export const getLastStructureVerdict = (): StructureVerdict => lastStructureVerdict;

async function verifyStructure(
  originalImage: string,
  editedImage: string,
  mode: 'verify-structure' | 'verify-renovation-structure' = 'verify-structure',
): Promise<StructureVerdict> {
  try {
    const [original, edited] = await Promise.all([
      downscaleForVerify(originalImage),
      downscaleForVerify(editedImage),
    ]);
    const r = await fetch(FN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        apikey: SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ mode, images: [original, edited] }),
    });
    const data = await r.json().catch(() => ({}) as any);
    if (!r.ok || !data?.verdict) return { ok: true, violations: [] };
    return {
      ok: data.verdict.ok !== false,
      violations: Array.isArray(data.verdict.violations) ? data.verdict.violations : [],
    };
  } catch (e) {
    console.warn('Structural verification unavailable, accepting result:', e);
    return { ok: true, violations: [] };
  }
}

const structureCorrectionPrompt = (violations: string[]): string =>
  '\n\nYOUR PREVIOUS ATTEMPT AT THIS EDIT WAS AUTOMATICALLY REJECTED because it altered the room instead of only adding furniture. An automated inspection comparing your output to the source photo found these violations:\n' +
  violations.map((v) => `- ${v}`).join('\n') +
  '\nRegenerate the edit and fix EVERY violation listed above. THIS IS AN IN-PLACE EDIT, NOT A RE-RENDER: keep the EXACT same camera position, viewing angle, lens, field of view, zoom, and framing as the source photo — do NOT move, rotate, zoom, pull back, or re-compose the camera. Every wall, ceiling beam, kitchen unit, door (including open door slabs), doorway, window, and built-in fixture visible in the source photo MUST appear in your output in the EXACT same position, size, and angle relative to the frame, unchanged and NOT hidden behind added furniture. Do NOT convert any door, doorway, or window into wall. The ONLY difference between the source photo and your output must be the added/removed movable furniture and decor.';

const renovationStructureCorrectionPrompt = (violations: string[]): string =>
  '\n\nYOUR PREVIOUS FULL RENOVATION WAS AUTOMATICALLY REJECTED because it redesigned the shell instead of finishing it. An automated inspection comparing your output to the unfinished source found:\n' +
  violations.map((v) => `- ${v}`).join('\n') +
  '\nRegenerate and fix EVERY violation. Finish THIS photographed shell in place:' +
  ' lock every doorway/opening at its current left and right edges;' +
  ' do NOT fill an open doorway with wall or invent a fitted door slab where the source only had an opening;' +
  ' do NOT turn a shallow view through the doorway (studs / next unfinished room) into a deep hallway or corridor — keep the same depth;' +
  ' lock every window at its current size and position; keep the view through each opening;' +
  ' do NOT invent hallways, extra doors, or a downlight grid;' +
  ' keep the same camera, room volume, and wall corners. Paint, flooring, fixtures, and furniture are allowed — moving or inventing openings/hallways is not.';

const furnitureCorrectionPrompt = (violations: string[]): string =>
  '\n\nYOUR PREVIOUS ATTEMPT WAS REJECTED because the furniture did not match the inventory checklist for this room. An automated inspection found:\n' +
  violations.map((v) => `- ${v}`).join('\n') +
  '\nRegenerate and fix EVERY listed issue. Keep IMAGE 1\'s architecture and camera exactly. Place the SAME checklist pieces (same type, colour, material, relative positions) that belong in this viewpoint. Do not invent substitute furniture.';

/**
 * Generate, then inspect for architectural changes. On failure, regenerate
 * with the inspector's findings. Across retries we keep the attempt with the
 * fewest violations (not blindly the last) so retries cannot make the result
 * worse and still win.
 */
async function runGeminiWithStructureCheck(
  opts: RunOpts,
  sourceImage: string,
  options?: { renovation?: boolean },
): Promise<string> {
  const renovation = !!options?.renovation;
  const verifyMode = renovation ? 'verify-renovation-structure' : 'verify-structure';
  const correction = renovation ? renovationStructureCorrectionPrompt : structureCorrectionPrompt;

  lastStructureVerdict = { ok: true, violations: [] };
  let bestResult = await runGemini(opts);
  let bestVerdict = await verifyStructure(sourceImage, bestResult, verifyMode);
  if (bestVerdict.ok) {
    lastStructureVerdict = bestVerdict;
    return bestResult;
  }

  let result = bestResult;
  for (let attempt = 0; attempt < MAX_STRUCTURE_RETRIES; attempt++) {
    console.warn(
      `${renovation ? 'Renovation' : 'Structural'} inspection failed (attempt ${attempt + 1}/${MAX_STRUCTURE_RETRIES}), regenerating:`,
      bestVerdict.violations,
    );
    result = await runGemini({
      ...opts,
      prompt: opts.prompt + correction(bestVerdict.violations),
    });
    const verdict = await verifyStructure(sourceImage, result, verifyMode);
    if (verdict.ok) {
      lastStructureVerdict = verdict;
      return result;
    }
    // Prefer fewer violations; on a tie keep the earlier (usually less over-corrected) attempt.
    if (verdict.violations.length < bestVerdict.violations.length) {
      bestResult = result;
      bestVerdict = verdict;
    }
  }

  lastStructureVerdict = bestVerdict;
  if (!bestVerdict.ok) {
    console.warn(
      `${renovation ? 'Renovation' : 'Structural'} inspection still failing after retries, returning least-bad attempt:`,
      bestVerdict.violations,
    );
  }
  return bestResult;
}

interface FurnitureVerdict {
  ok: boolean;
  violations: string[];
}

async function verifyFurniture(
  stagedImage: string,
  inventory: FurnitureInventory,
): Promise<FurnitureVerdict> {
  try {
    const edited = await downscaleForVerify(stagedImage);
    const r = await fetch(FN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        apikey: SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        mode: 'verify-furniture',
        images: [edited],
        inventory,
      }),
    });
    const data = await r.json().catch(() => ({}) as any);
    if (!r.ok || !data?.verdict) return { ok: true, violations: [] };
    return {
      ok: data.verdict.ok !== false,
      violations: Array.isArray(data.verdict.violations) ? data.verdict.violations : [],
    };
  } catch (e) {
    console.warn('Furniture verification unavailable, accepting result:', e);
    return { ok: true, violations: [] };
  }
}

/**
 * After a structure-clean result, optionally verify furniture identity
 * against the hero inventory and regenerate on clear mismatches.
 */
async function runGeminiWithFurnitureCheck(
  opts: RunOpts,
  sourceImage: string,
  inventory: FurnitureInventory,
): Promise<string> {
  let result = await runGeminiWithStructureCheck(opts, sourceImage);
  if (!inventory.pieces.length) return result;

  for (let attempt = 0; attempt < MAX_FURNITURE_RETRIES; attempt++) {
    const verdict = await verifyFurniture(result, inventory);
    if (verdict.ok) return result;
    console.warn(
      `Furniture inspection failed (attempt ${attempt + 1}/${MAX_FURNITURE_RETRIES}), regenerating:`,
      verdict.violations,
    );
    result = await runGeminiWithStructureCheck(
      {
        ...opts,
        prompt: opts.prompt + furnitureCorrectionPrompt(verdict.violations),
      },
      sourceImage,
    );
  }
  const final = await verifyFurniture(result, inventory);
  if (!final.ok) {
    console.warn(
      'Furniture inspection still failing after retries, returning last attempt:',
      final.violations,
    );
  }
  return result;
}

interface PlacementPlan {
  notes: string;
  pieces: Array<{ id: string; visible: boolean; placement: string }>;
}

/**
 * Text-model pass that sees BOTH the unstaged target angle and the staged
 * hero, and maps each inventory piece into the TARGET's frame ("beige sofa
 * along the left wall, seen from its right end") or marks it out of frame.
 * This moves the hard cross-view spatial reasoning into a model that is good
 * at it, so the image model receives concrete directives instead of having
 * to solve the 3D correspondence mid-generation. Fails open (null).
 */
async function planFurniturePlacement(
  targetImage: string,
  anchorImage: string,
  inventory: FurnitureInventory,
): Promise<PlacementPlan | null> {
  try {
    const [target, anchor] = await Promise.all([
      downscaleForVerify(targetImage),
      downscaleForVerify(anchorImage),
    ]);
    const r = await fetch(FN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        apikey: SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        mode: 'plan-placement',
        images: [target, anchor],
        inventory,
      }),
    });
    const data = await r.json().catch(() => ({}) as any);
    if (!r.ok || !data?.plan?.pieces?.length) return null;
    return {
      notes: typeof data.plan.notes === 'string' ? data.plan.notes : '',
      pieces: data.plan.pieces,
    };
  } catch (e) {
    console.warn('Placement plan unavailable, falling back to checklist only:', e);
    return null;
  }
}

/**
 * Extract a structured furniture inventory from a staged hero image
 * (text-only vision pass). Returns null on failure so callers can fall
 * back to visual-only anchoring.
 */
export const extractFurnitureInventory = async (
  stagedHeroImage: string,
): Promise<FurnitureInventory | null> => {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return null;
  try {
    const thumb = await downscaleForVerify(stagedHeroImage);
    const r = await fetch(FN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        apikey: SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ mode: 'extract-inventory', images: [thumb] }),
    });
    const data = await r.json().catch(() => ({}) as any);
    if (!r.ok || !data?.inventory) return null;
    const pieces = Array.isArray(data.inventory.pieces) ? data.inventory.pieces : [];
    if (pieces.length === 0) return null;
    return {
      styleNotes: typeof data.inventory.styleNotes === 'string' ? data.inventory.styleNotes : '',
      pieces: pieces.map((p: any, i: number) => ({
        id: typeof p.id === 'string' && p.id ? p.id : `piece-${i + 1}`,
        name: String(p.name || 'furniture'),
        colour: typeof p.colour === 'string' ? p.colour : '',
        material: typeof p.material === 'string' ? p.material : '',
        position: typeof p.position === 'string' ? p.position : '',
      })),
    };
  } catch (e) {
    console.warn('Furniture inventory extract failed:', e);
    return null;
  }
};

async function runGemini(opts: RunOpts): Promise<string> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error(
      'Image service not configured: VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY missing.',
    );
  }

  const trial = activeImageModel;
  const useOpenAI = trial === 'gpt-sunburst';
  const endpoint = useOpenAI ? OPENAI_FN_URL : FN_URL;
  const geminiModel = useOpenAI ? undefined : geminiModelForTrial(trial);

  let lastError: any;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      if (attempt > 0) {
        const delay = INITIAL_DELAY_MS * Math.pow(2, attempt - 1);
        console.log(`Image gen retry ${attempt + 1}/${MAX_RETRIES} after ${delay}ms (${trial})`);
        await sleep(delay);
      }

      const body: Record<string, unknown> = {
        prompt: opts.prompt,
        images: opts.images,
        aspect_ratio: normaliseAspect(opts.aspectRatio),
        resolution: opts.resolution || '4K',
        output_format: 'png',
      };
      if (geminiModel) body.model = geminiModel;
      if (useOpenAI) {
        body.model = 'gpt-image-2.5-sunburst';
        body.quality = 'high';
      }

      const r = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(body),
      });

      const data = await r.json().catch(() => ({}) as any);
      if (!r.ok || !data?.imageUrl) {
        throw new Error(data?.error || `Edge function error ${r.status}`);
      }

      return await urlToDataUrl(data.imageUrl);
    } catch (e: any) {
      lastError = e;
      const msg = e?.message || String(e);
      console.error(`Image gen attempt ${attempt + 1} failed (${trial}):`, msg);
      if (!isRetryableError(msg) || attempt === MAX_RETRIES - 1) throw e;
    }
  }
  throw lastError;
}

/**
 * Single-image edit. The mask is burnt into the source image as a
 * red overlay (see Editor.tsx); the prompt guardrails tell the model
 * to treat red pixels as a control signal and not carry the colour
 * through to the output.
 */
export const declutterImage = async (
  imageBase64: string,
  promptText: string,
  aspectRatio: string = '1:1',
): Promise<string> => {
  const renovation = isFullRenovationPrompt(promptText);
  const stagingExtras = renovation
    ? ' CRITICAL FOR FULL RENOVATION — FINISH THIS SHELL, DO NOT REDESIGN IT:' +
      ' Keep the EXACT camera, lens, field of view, and framing. Do NOT crop, zoom, pan, tilt, or recompose.' +
      ' Lock every window at its CURRENT size and pixel position — a small/high window stays small/high; never stretch it into a picture window.' +
      ' Lock every doorway/opening at its CURRENT left and right edges — do not slide, centre, widen, fill with wall, invent a fitted door slab, or turn a shallow view through the opening into a deep hallway.' +
      ' Keep the view through each opening at the SAME depth; finish it in place. Do NOT invent windows, doors, corridors, or a different room layout.' +
      ' Ceiling lights ONLY where the source already shows a rose, pendant drop, downlight hole, or back-box — no new downlight grids or decorative pendants.' +
      ' Finish walls, floors, and room-type fixtures; then furnish. The result must overlay the source on openings.' +
      ' Finish ALL the way to the top of the frame — do NOT leave a strip of original joists, plasterboard, or construction ceiling.'
    : isStagingPrompt(promptText)
    ? ' CRITICAL FOR STAGING — THIS IS AN IN-PLACE EDIT, NOT A RE-RENDER:' +
      ' The output MUST be the SAME photo, shot from the SAME camera position, with the SAME lens,' +
      ' the SAME field of view, and the SAME framing. Do NOT change the camera angle, perspective,' +
      ' focal length, FOV, or composition. Do NOT crop, zoom in, zoom out, pan, tilt, level, or' +
      ' recompose the image. The four image edges MUST contain the same scene content as the input.' +
      ' Do not change walls, floor, ceiling, or window structure. Do not alter wall texture or floor' +
      ' surface. Do NOT change the view through any windows — keep the exact original scene visible' +
      ' through each window (do NOT replace with blue sky or any other view). Only add furniture and' +
      ' decor (including curtains) inside the existing room. A viewer comparing the output to the' +
      ' input side-by-side MUST see the same walls in the same pixel positions and the same vanishing' +
      ' points — only the added furniture should differ.'
    : ' STRICT REQUIREMENT — THIS IS AN IN-PLACE EDIT, NOT A RE-RENDER:' +
      ' Maintain the EXACT framing, perspective, lighting, focal length, field of view, and camera' +
      ' angle of the original image. Do NOT crop, zoom in, zoom out, pan, tilt, level, recompose, or' +
      ' otherwise re-frame the image. The four image edges MUST contain the same scene content as' +
      ' the input. Do not distort the room geometry. Perform the requested changes naturally while' +
      ' preserving the structural integrity of the room and the exact camera position.';

  const maskHint =
    ' Any red overlay or red brush marks in the input image are a control signal marking the' +
    ' area to change — do NOT carry the red colour into the output; replace those pixels with' +
    ' the requested edit.';

  const colorRules = renovation
    ? ' LIGHTING: photorealistic photograph of THIS finished room from the SAME camera — natural, not CGI,' +
      ' not a brochure render of a different layout. White balance should suit the finished materials, not the unfinished' +
      ' plaster or plasterboard of the source photo.'
    : COLOR_FIDELITY_REQUIREMENTS;

  const fullPrompt = `${promptText}${stagingExtras}${maskHint}${colorRules}`;

  const opts: RunOpts = {
    prompt: fullPrompt,
    images: [imageBase64],
    aspectRatio,
    resolution: await resolutionForImage(imageBase64),
  };

  if (renovation) {
    // Renovation may change finishes, but openings / walls / camera must
    // survive. Use the renovation inspector (staging inspector would reject
    // legitimate paint/floor/fixture changes).
    return runGeminiWithStructureCheck(opts, imageBase64, { renovation: true });
  }

  return isStructurePreservingEdit(promptText)
    ? runGeminiWithStructureCheck(opts, imageBase64)
    : runGemini(opts);
};

/**
 * Two-image edit using a SEPARATE binary mask. White pixels in the mask
 * mark the only region to modify; black pixels must be preserved.
 *
 * nano-banana-pro accepts up to 14 input images — we send the source
 * first, the mask second, and instruct the model to treat the mask as
 * a control image rather than scene content.
 */
export const editImageWithMask = async (
  sourceImageBase64: string,
  maskImageBase64: string,
  promptText: string,
  aspectRatio: string = '1:1',
): Promise<string> => {
  const guidance =
    ' Image 1 is the SOURCE photo. Image 2 is a BINARY MASK at the same dimensions:' +
    ' WHITE pixels mark the ONLY region to modify, BLACK pixels MUST be preserved EXACTLY' +
    ' pixel-for-pixel from Image 1. The mask is a control signal — it is NOT part of the scene.' +
    ' The output MUST NOT contain ANY pure red, white, or black overlay from the mask.' +
    ' Outside the white region, the output must be visually identical to Image 1' +
    ' (same colours, same white balance, no global tint).';

  return runGemini({
    prompt: `${promptText}${guidance}${COLOR_FIDELITY_REQUIREMENTS}`,
    images: [sourceImageBase64, maskImageBase64],
    aspectRatio,
    resolution: await resolutionForImage(sourceImageBase64),
  });
};

/**
 * Brush repair for invented walls/doors after Full Renovation.
 * Image 1 = current renovated result, Image 2 = binary mask of the bad
 * region, Image 3 = original unfinished shell (architecture reference).
 * Outside the mask, compositeMaskedResult in App.tsx restores Image 1.
 */
export const repairStructureRegion = async (
  currentImageBase64: string,
  maskImageBase64: string,
  originalShellBase64: string,
  promptText: string,
  aspectRatio: string = '1:1',
): Promise<string> => {
  const guidance =
    ' Image 1 is the CURRENT renovated photo (canvas). Image 2 is the BINARY MASK' +
    ' (WHITE = repair region only; BLACK = preserve Image 1 exactly). Image 3 is the' +
    ' ORIGINAL unfinished shell — architecture reference only for the white region.' +
    ' Restore openings / doorway edges / views from Image 3 inside the mask; do NOT' +
    ' invent door slabs where Image 3 had an open doorway. Outside the white region,' +
    ' keep Image 1 pixel-identical. Never paint mask colours into the output.';

  return runGemini({
    prompt: `${promptText}${guidance}${COLOR_FIDELITY_REQUIREMENTS}`,
    images: [currentImageBase64, maskImageBase64, originalShellBase64],
    aspectRatio,
    resolution: await resolutionForImage(currentImageBase64),
  });
};

/**
 * Two-image edit using a reference photo for style/setup matching
 * (e.g. a staged photo of a different room). The model must output the
 * TARGET image with the reference's furniture style applied — not the
 * reference re-angled, and not the camera re-framed.
 *
 * Two production-tested levers are combined here to get reliable
 * in-place edits out of `gemini-3-pro-image-preview`:
 *
 *   1. IMAGE ORDER — image-edit models anchor most strongly to the
 *      first image. We send the TARGET first (the canvas) and the
 *      REFERENCE second (the mood-board). The prompts
 *      (`MATCH_STYLE_PROMPT`, `MATCH_STYLE_360_PROMPT`) are written
 *      to match this order. Sending the reference first used to make
 *      Gemini effectively output the reference with minor edits, which
 *      read as "the reference badly composited onto the empty room".
 *
 *   2. RESOLUTION ASYMMETRY — the caller is expected to downscale the
 *      reference to a small thumbnail (~384px) before passing it in.
 *      The target stays at full processing resolution. This creates an
 *      implicit "the high-res image is the canvas, the low-res image
 *      is a swatch" signal that prompt language alone could not
 *      enforce, and stops the model from pixel-transplanting the
 *      reference's walls / windows / framing onto the target. See
 *      `prepareReferenceForGemini` in `App.tsx`.
 */
export const declutterImageWithReference = async (
  targetImageBase64: string,
  referenceImageBase64: string,
  promptText: string,
  aspectRatio: string = '1:1',
): Promise<string> => {
  const guidance =
    ' CRITICAL — IMAGE ROLES:' +
    ' Image 1 is the TARGET (high-resolution). It is the ONLY canvas you may edit and the ONLY image' +
    ' whose pixels may appear in the output.' +
    ' Image 2 is the REFERENCE (deliberately provided as a small low-resolution thumbnail because it' +
    ' is a STYLE SWATCH, not a scene). Read off furniture types, colours, materials, and rough' +
    ' arrangement from it, then forget its pixels. Do NOT upsample Image 2. Do NOT copy, paste,' +
    ' overlay, composite, blend, stitch, layer, warp, re-project, or re-angle any region of Image 2' +
    ' into Image 1. Image 2\'s walls, windows, doors, floor, ceiling, lighting, and framing are' +
    ' IRRELEVANT — only its FURNITURE STYLE is used.' +
    ' THIS IS AN IN-PLACE EDIT OF IMAGE 1, NOT A RE-RENDER. The output MUST be Image 1, shot from' +
    ' the EXACT same camera position, with the EXACT same lens, focal length, FOV, framing, and crop.' +
    ' Do NOT change Image 1\'s camera angle, perspective, vanishing points, or composition. Do NOT' +
    ' zoom, pan, tilt, level, straighten, widen, or "improve" the framing. The four image edges of' +
    ' the output MUST contain the same scene content as the four image edges of Image 1. Only add' +
    ' furniture and decor to Image 1; do not change its walls, floor, ceiling, windows, doors,' +
    ' radiators, fittings, or the view through any windows.' +
    ' If a reference piece doesn\'t fit Image 1\'s actual wall lengths or doorway clearances, pick a' +
    ' smaller or differently shaped piece in the same style — do not force the reference\'s exact' +
    ' piece in.';

  return runGeminiWithStructureCheck(
    {
      prompt: `${promptText}${guidance}${COLOR_FIDELITY_REQUIREMENTS}`,
      images: [targetImageBase64, referenceImageBase64],
      aspectRatio,
      resolution: await resolutionForImage(targetImageBase64),
    },
    targetImageBase64,
  );
};

/**
 * Two-image edit for staging MULTIPLE ANGLES OF THE SAME ROOM.
 *
 * Unlike `declutterImageWithReference` (which treats Image 2 as a low-res
 * STYLE swatch and explicitly throws its layout away), here Image 2 is the
 * SAME room already staged, supplied at HIGH resolution as a LAYOUT ANCHOR.
 * The goal is cross-angle consistency: reproduce the SAME individual
 * furniture pieces in the SAME real-world positions, re-projected into the
 * target's camera — so a buyer flipping between the photos sees one
 * consistently furnished room rather than two rooms that merely share a
 * style.
 *
 * Same two production levers as the reference flow, but inverted:
 *   1. IMAGE ORDER — TARGET first (the canvas / new angle), ANCHOR second.
 *   2. RESOLUTION — the anchor is deliberately kept at (near) full
 *      resolution by the caller (`prepareAnchorForGemini` in App.tsx),
 *      because here we WANT the model to read the furniture layout in
 *      detail. The prompt still forbids transplanting Image 2's pixels /
 *      architecture; only the furniture identity + arrangement transfers.
 */
export const declutterImageWithRoomAnchor = async (
  targetImageBase64: string,
  anchorImageBase64: string,
  promptText: string,
  aspectRatio: string = '1:1',
  inventory?: FurnitureInventory | null,
): Promise<string> => {
  // Short by design — the task/architecture split lives in the main prompt
  // (SAME_ROOM_ANGLE_*); repeating "change nothing" walls of text here was
  // pushing the model into returning the room empty.
  const guidance =
    ' REMEMBER THE TWO FAILURE MODES, BOTH WRONG:' +
    ' (1) outputting Image 2 (or a warped version of it) instead of editing Image 1;' +
    ' (2) outputting Image 1 unchanged or nearly unchanged — an empty room is a failed result.' +
    ' The correct output is Image 1\'s room and camera with Image 2\'s furniture set drawn into it.';

  const inventoryBlock = inventory?.pieces?.length
    ? formatFurnitureInventoryChecklist(inventory)
    : '';

  // Per-angle placement plan: resolves the cross-view spatial reasoning in a
  // text pass so the image model gets concrete "draw X here" directives.
  let planBlock = '';
  if (inventory?.pieces?.length) {
    const plan = await planFurniturePlacement(targetImageBase64, anchorImageBase64, inventory);
    if (plan) planBlock = formatPlacementPlan(plan);
  }

  const opts: RunOpts = {
    prompt: `${promptText}${inventoryBlock}${planBlock}${guidance}${COLOR_FIDELITY_REQUIREMENTS}`,
    images: [targetImageBase64, anchorImageBase64],
    aspectRatio,
    resolution: await resolutionForImage(targetImageBase64),
  };

  if (inventory?.pieces?.length) {
    return runGeminiWithFurnitureCheck(opts, targetImageBase64, inventory);
  }
  return runGeminiWithStructureCheck(opts, targetImageBase64);
};

/** Upscales the provided image to 4K, preserving composition exactly. */
export const upscaleImage = async (imageBase64: string): Promise<string> => {
  return runGemini({
    prompt:
      'Upscale this image to high quality 4K resolution. Improve sharpness, lighting, ' +
      'and texture details while maintaining the original composition and content exactly. ' +
      'Do not add, remove, or rearrange any objects. Do not change the colour balance.',
    images: [imageBase64],
    aspectRatio: 'match_input_image',
  });
};
